import React, { useContext } from 'react';
import { getResizedImageUrl } from '@lib/image';
import BoardImagesModalContext from '@context/Modal/board-images-modal-context';
import { IDesignerProfile } from '@components/pages/DesignerProfile/graphql/designer-profile-query.graphql';
import { IDesignerBoard } from '@components/pages/DesignerProfile/graphql/designer-boards.graphql';
import Loader from '@components/shared/Loader/Loader';
import { useTheme } from '@components/theme';
import classNames from 'classnames';
import useBoards from './useBoards';

import styles from './Boards.module.scss';

interface IBoardsProps {
  initialBoards: IDesignerBoard[];
  designer: IDesignerProfile;
}

const Boards = ({ initialBoards, designer }: IBoardsProps) => {
  const theme = useTheme();
  const { open } = useContext(BoardImagesModalContext);
  const {
    loading,
    fetchMore,
    boards,
    exhausted
  } = useBoards({ designerId: designer.id, initialBoards });

  const boardHandler = (boardId: string) => { open({ boards, boardId }); };
  return (
    <div className={styles.Container} data-test="portfolio">
      {boards.map((board) => (
        <Board
          board={board}
          key={board.id}
          designer={designer}
          onClick={boardHandler}
        />
      ))}

      { loading && (
        <div className={ styles.Center }>
          <Loader />
        </div>
      )}

      { !exhausted && (
        <div className={ styles.Center }>
          <button
            type="button"
            onClick={fetchMore}
            data-test="load-more"
            className={classNames(
              theme.styles.Primary,
              theme.styles.Button,
              theme.styles.Small
            )}
          >
            load more
          </button>
        </div>
      )}

    </div>
  );
};

interface IBoardProps {
  board: IDesignerBoard;
  designer: IDesignerProfile;
  onClick: (boardId: string) => void;
}

function Board({ board, designer, onClick }: IBoardProps) {
  const { mainBoardAsset } = board;

  return (
    <div className={ styles.Board } data-test="portfolio-item">
      <div className={ styles.Image }>
        <button
          className={ styles.Button }
          type="button"
          onClick={() => { onClick(board.id); }}
        >
          <picture>
            { ['webp', 'jpeg'].map((format) => (
              <React.Fragment key={format}>
                <source
                  srcSet={getResponsiveImages(mainBoardAsset?.asset?.url, 700, 700, format)}
                  media="(min-width: 1559px)"
                />
                <source
                  srcSet={getResponsiveImages(mainBoardAsset?.asset?.url, 532, 532, format)}
                  media="(min-width: 1100px)"
                />
                <source
                  srcSet={getResponsiveImages(mainBoardAsset?.asset?.url, 757, 506, format)}
                  media="(min-width: 768px)"
                />
                <source
                  srcSet={getResponsiveImages(mainBoardAsset?.asset?.url, 350, 222, format)}
                />
              </React.Fragment>
            ))}
            <img
              alt={getImageAlt(board, designer)}
              src={getThumborURL(mainBoardAsset?.asset?.url, 330, 220, 'jpeg')}
            />
          </picture>
        </button>
      </div>
    </div>
  );
}

function getImageAlt(board: IDesignerBoard, designer: IDesignerProfile) {
  const alt = [];
  alt.push(board.subStyles.map(({ name }) => name).join(', '));

  if (board.room) {
    alt.push(board.room.roomType?.type);
  }

  alt.push(`by Havenly Interior Designer ${designer.firstName}`);

  return alt.join(' ');
}

function getResponsiveImages(
  image: string|undefined,
  width: number,
  height: number,
  format: string
) {
  if (!image) return '';
  const main = [`${getThumborURL(image, width, height, format)} ${width}w`];
  main.push(`${getThumborURL(image, width * 2, height * 2, format)} 2x`);

  return main.join(', ');
}

function getThumborURL(
  url: string | undefined,
  width: number,
  height: number,
  format: string
): string {
  if (!url) return '';
  return getResizedImageUrl({
    format,
    quality: 90,
    url,
    width,
    height,
    fit: 'fit-in'
  });
}

export default Boards;
